import {useGrpcRequestMutation} from '@aboutyou/data-fetcher';
import {BasketPageService_ToggleCoinsLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/page/service.grpc.lazy';
import {ToggleCoinsResponse_EnabledHint} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/page/service.types';
import {useBasketToken, useCustomerSession} from '@aboutyou/user';
import {startTransition, useState} from 'react';
import {useReductionsStateRequestParameter} from '../../../../features/ReductionsState/useReductionsStateRequestParameter';
import {useTargetGroupIdentifier} from '../../../../shared-deprecated/useTargetGroupIdentifier';
// eslint-disable-next-line jest/no-mocks-import
import {useUpdateBasketSectionsCallback} from '../../hooks/useUpdateBasketSections';

const REDEEM_COINS_POPUP_STEP_DURATION = 4000;

export enum RedeemCoinsStep {
  Loading,
  Preparing,
  Applied,
  Error,
}

export const useRedeemCoins = () => {
  const {token: basketToken} = useBasketToken();

  const [step, setStep] = useState<RedeemCoinsStep | undefined>();
  const [isRedeemCoinsPopupShown, setShowRedeemCoinsPopup] = useState(false);
  const [redeemCoinsPopupData, setRedeemCoinsPopupData] = useState<
    ToggleCoinsResponse_EnabledHint | undefined
  >();
  const session = useCustomerSession();
  const updatePageBulkSections = useUpdateBasketSectionsCallback();
  const targetGroup = useTargetGroupIdentifier();
  const reductionsState = useReductionsStateRequestParameter();

  const delayedSetStatus = async (
    status: RedeemCoinsStep,
    delay = REDEEM_COINS_POPUP_STEP_DURATION,
  ) => {
    return new Promise<void>(resolve => {
      setTimeout(() => {
        setStep(status);
        resolve();
      }, delay);
    });
  };

  const redeemCoinsRequest = useGrpcRequestMutation(
    BasketPageService_ToggleCoinsLazy,
    {disabled: !session},
  );

  const redeemCoins = async (options: {
    toggleTransition: boolean;
    onError?: VoidFunction;
  }) => {
    if (!options.toggleTransition) {
      await redeemCoinsRequest.mutateAsync({
        session: session!,
        targetGroup,
        reductionsState,
        basketToken,
      });

      return;
    }

    startTransition(() => {
      setStep(RedeemCoinsStep.Loading);
      setShowRedeemCoinsPopup(true);
    });

    try {
      const {sections, enabledHint} = await redeemCoinsRequest.mutateAsync({
        session: session!,
        targetGroup,
        reductionsState,
        basketToken,
      });

      setRedeemCoinsPopupData(enabledHint);
      await delayedSetStatus(RedeemCoinsStep.Preparing);
      updatePageBulkSections(sections);
      await delayedSetStatus(RedeemCoinsStep.Applied, 800);
    } catch (error) {
      await delayedSetStatus(RedeemCoinsStep.Error);
      options.onError?.();
    }

    // auto close the popup
    await new Promise<void>(resolve =>
      // eslint-disable-next-line no-promise-executor-return
      setTimeout(() => {
        setShowRedeemCoinsPopup(false);
        resolve();
      }, REDEEM_COINS_POPUP_STEP_DURATION),
    );
  };

  return {
    redeemCoins,
    redeemCoinsPopupData,
    step,
    isRedeemCoinsPopupShown,
  };
};
